import React, { useContext, forwardRef } from "react";
import PropTypes from "prop-types";
import styled, { ThemeContext } from "styled-components";
import { Link } from "react-router-dom";
import { LOGO_LINK } from "./NavbarConstants";

const Img = styled.img`
  align-self: center;
  display: block;
  max-width: 100%;
  max-height: 40px;
  height: auto;
  width: auto;
`;

const Anchor = styled.a`
  display: flex;
  overflow: hidden;
  max-width: 300px;
  height: 100%;
  justify-content: center;
  align-items: center;
  outline-offset: -3px;
  margin-right: 32px;
`;

const InternalAnchor = styled(({ isMobile, ...rest }) => <Link {...rest} />)`
  display: flex;
  overflow: hidden;
  max-width: 300px;
  height: 100%;
  justify-content: center;
  align-items: center;
  outline-offset: -3px;
  margin-right: 32px;
`;

const Logo = forwardRef(({ isLogoLinkInternal }, ref) => {
  const theme = useContext(ThemeContext);
  const bDefaultImg = theme.logoUrl === "/images/logo_default@2x.png";

  const logo = bDefaultImg ? (
    <ImgSvg fillColor={theme.textColor} />
  ) : (
    <Img alt="" src={theme.logoUrl} />
  );

  //NOTE: LOGO_LINK is not tied to aria-label; we assume logo link always points to portal
  return (
    <div ref={ref}>
      {isLogoLinkInternal ? (
        <InternalAnchor to={LOGO_LINK} aria-label="portal">
          {logo}
        </InternalAnchor>
      ) : (
        <Anchor href={LOGO_LINK} aria-label="portal">
          {logo}
        </Anchor>
      )}
    </div>
  );
});

Logo.propTypes = {
  isLogoLinkInternal: PropTypes.bool.isRequired
};

export default Logo;

function ImgSvg({ fillColor }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height="40"
      width="98"
      viewBox="0 0 98 40"
    >
      <path
        fill={fillColor}
        fillRule="nonzero"
        d="M32.165 18.83c.219-1.38 1.435-3.095 3.757-3.095 2.153 0 3.397 1.577 3.593 3.095h-7.35zm-2.986 1.106c0 3.813 2.762 6.41 6.798 6.41 2.652 0 4.393-1.3 5.417-2.433.105-.114.165-.274 0-.443l-1.421-1.312c-.165-.165-.325-.105-.444 0-.722.635-1.838 1.645-3.552 1.645-1.988 0-3.428-1.216-3.758-2.76h9.838c.274 0 .444-.165.444-.444v-.608c0-3.648-2.378-6.688-6.41-6.688-4.342 0-6.912 2.962-6.912 6.633zm15.086 5.856c0 .22.11.334.334.334h2.098c.22 0 .334-.11.334-.334V8.274c0-.164-.055-.274-.274-.274h-2.213c-.22 0-.274.11-.274.274v17.518h-.005zm-19.451 0c0 .22.11.334.333.334h2.099c.22 0 .333-.11.333-.334v-7.017c0-2.985-1.769-5.472-5.526-5.472h-.055c-1.824 0-3.205.608-4.032 1.326v-.663c0-.22-.165-.334-.334-.334h-2.098c-.22 0-.334.11-.334.334v11.826c0 .22.11.334.334.334h2.098c.22 0 .334-.11.334-.334v-6.519c0-2.043 1.051-3.538 3.483-3.538 2.432 0 3.37 1.49 3.37 3.538v6.519h-.005zm39.803 3.694c.965 1.129 2.994 2.496 5.847 2.496 3.986 0 6.41-2.432 6.41-6.3V13.966c0-.22-.11-.334-.335-.334h-1.988c-.22 0-.334.11-.334.334v.663c-.882-.718-2.043-1.326-3.922-1.326-3.593 0-6.743 2.651-6.743 6.519 0 3.922 3.04 6.519 6.743 6.519 1.737 0 2.953-.636 3.812-1.308v.814c0 2.212-1.284 3.648-3.67 3.648-1.98 0-3.292-1.074-4.124-1.879-.169-.165-.388-.165-.553 0l-1.147 1.317c-.165.164-.165.329.004.553zM10.61 19.822c0 2.212-1.66 3.977-3.867 3.977-2.208 0-3.758-1.879-3.758-3.977v-.11c0-2.098 1.545-3.867 3.758-3.867 2.212 0 3.867 1.769 3.867 3.977zm-10.61 0c0 3.538 2.875 6.519 6.743 6.519 3.867 0 6.743-2.981 6.743-6.52v-.109c0-3.538-2.876-6.41-6.743-6.41-3.868 0-6.743 2.876-6.743 6.52zm59.3 0c0 2.212-1.659 3.977-3.867 3.977-2.212 0-3.758-1.879-3.758-3.977v-.11c0-2.098 1.546-3.867 3.758-3.867 2.208 0 3.868 1.769 3.868 3.977zm-10.614 0c0 3.538 2.875 6.519 6.743 6.519 3.867 0 6.742-2.986 6.742-6.52v-.109c0-3.538-2.875-6.41-6.742-6.41-3.868 0-6.743 2.876-6.743 6.52zm30.784 5.97c0 .22.11.334.333.334h2.099c.22 0 .333-.11.333-.334V13.911c0-.165-.054-.274-.274-.274H79.75c-.22 0-.275.11-.275.274v11.881h-.004zm0-15.31c0 .22.11.334.333.334h2.099c.22 0 .333-.11.333-.334V8.274c0-.164-.054-.274-.274-.274H79.75c-.22 0-.275.11-.275.274v2.208h-.004zm14.92 15.31c0 .22.11.334.335.334h2.098c.22 0 .334-.11.334-.334v-7.017c0-2.985-1.77-5.472-5.527-5.472h-.055c-1.824 0-3.205.608-4.032 1.326v-.663c0-.22-.165-.334-.334-.334h-2.098c-.22 0-.334.11-.334.334v11.826c0 .22.11.334.334.334h2.098c.22 0 .334-.11.334-.334v-6.519c0-2.043 1.051-3.538 3.483-3.538 2.432 0 3.37 1.49 3.37 3.538v6.519h-.005zm-20.228-5.97c0 2.212-1.66 3.977-3.867 3.977-2.213 0-3.758-1.879-3.758-3.977v-.11c0-2.098 1.545-3.867 3.758-3.867 2.212 0 3.867 1.769 3.867 3.977z"
      />
    </svg>
  );
}
